import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import clsx from "clsx";

import Sidebar from "../Sidebar";
import HamburgerMenu from "../HamburgerMenu";
import {
  teachingMaterialPath,
  activeLearningLessonPath,
  Context,
  updatePhoto,
  fetchInitialData,
  clearSuccessMessage,
} from "../../../studentStore";
import { IPhoto } from "../../../state";
import { parseQueryParams } from "../../../libs/Util";
import { UPDATE_PHOTO_SUCCESS_MESSAGE } from "../../../studentStore/messages";
import useWaitApiCall from "../../../hooks/useWaitApiCall";

import { UpdateProfileImageModal } from "./UpdateProfileImageModal";
import styles from "./CustomSidebar.module.scss";

export interface CustomSidebarProps {
  isLoaded: boolean;
  isDlpEnabled: boolean;
  isSurveyEnabled: boolean;
  isOnlyAssessment: boolean;
  isG4sEnabled: boolean;
  isDummy: boolean;
  availableItemCodes: string[];
  logout: () => void;
  isOnlyBase: boolean;
  isMasqueradeByTeacher: boolean;
}

const excludedPaths = [teachingMaterialPath, activeLearningLessonPath];

const CustomSidebar: React.FC<CustomSidebarProps> = ({
  isLoaded,
  isDlpEnabled,
  isSurveyEnabled,
  isOnlyAssessment,
  isG4sEnabled,
  isDummy,
  availableItemCodes,
  logout,
  isOnlyBase,
  isMasqueradeByTeacher,
}) => {
  const { state, dispatch } = useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const location = useLocation();
  const showDefaultSidebar = !new RegExp(excludedPaths.join("|")).test(
    location.pathname,
  );
  const hideSidebar = parseQueryParams()["sidebar"] === "no";
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (state.success_message === UPDATE_PHOTO_SUCCESS_MESSAGE) {
      dispatch(clearSuccessMessage());
      dispatch(fetchInitialData());
    }
  }, [state.success_message, dispatch]);

  const handleToggleMenu = (isOpen: boolean) => setIsSidebarOpen(isOpen);

  const handleProfileIconClick = useCallback(() => {
    if (calling) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [calling]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const MAX_SIZE = 20 * 1024 * 1024;
        if (file.size > MAX_SIZE) {
          alert("ファイルサイズは20MB以下にしてください");
        } else {
          setProfileFile(file);
          setShowProfileModal(true);
        }
      }
      event.target.value = "";
    },
    [],
  );

  const handleProfileSubmit = useCallback(
    (croppedFile: File) => {
      const currentPhoto = state.student?.photo;
      if (currentPhoto?.url && currentPhoto.url.startsWith("blob:")) {
        URL.revokeObjectURL(currentPhoto.url);
      }
      const photoData: IPhoto = {
        ...(currentPhoto || {}),
        name: croppedFile.name,
        url: URL.createObjectURL(croppedFile),
        blob: croppedFile,
        deleted: false,
      };

      dispatch(peep(updatePhoto(photoData)));
      setShowProfileModal(false);
      setProfileFile(null);
    },
    [dispatch, peep, state.student?.photo],
  );

  const handleProfileDelete = useCallback(() => {
    const currentPhoto = state.student?.photo;
    if (currentPhoto?.url && currentPhoto.url.startsWith("blob:")) {
      URL.revokeObjectURL(currentPhoto.url);
    }
    if (currentPhoto && currentPhoto.id) {
      const photoData: IPhoto = {
        ...currentPhoto,
        url: "",
        blob: undefined,
        deleted: true,
      };

      dispatch(peep(updatePhoto(photoData)));
    }
    setShowProfileModal(false);
    setProfileFile(null);
  }, [dispatch, peep, state.student?.photo]);

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/png, image/jpeg, image/webp"
        onChange={handleFileChange}
      />

      {showDefaultSidebar && !hideSidebar && (
        <Sidebar
          className={styles.sidebar}
          isLoaded={isLoaded}
          isDlpEnabled={isDlpEnabled}
          isSurveyEnabled={isSurveyEnabled}
          isOnlyAssessment={isOnlyAssessment}
          isG4sEnabled={isG4sEnabled}
          isDummy={isDummy}
          availableItemCodes={availableItemCodes}
          logout={logout}
          isOnlyBase={isOnlyBase}
          isMasqueradeByTeacher={isMasqueradeByTeacher}
          onProfileClick={handleProfileIconClick}
        />
      )}

      <HamburgerMenu
        className={clsx(
          styles.hamburgerMenuWrapper,
          !showDefaultSidebar &&
            !hideSidebar &&
            styles["hamburgerMenuWrapper--alwaysShow"],
        )}
        isOpen={isSidebarOpen}
        onToggle={handleToggleMenu}
      >
        <Sidebar
          className={showDefaultSidebar ? styles.overlaySidebar : undefined}
          isLoaded={isLoaded}
          isDlpEnabled={isDlpEnabled}
          isSurveyEnabled={isSurveyEnabled}
          isOnlyAssessment={isOnlyAssessment}
          isG4sEnabled={isG4sEnabled}
          isDummy={isDummy}
          availableItemCodes={availableItemCodes}
          logout={logout}
          isOnlyBase={isOnlyBase}
          isMasqueradeByTeacher={isMasqueradeByTeacher}
          onProfileClick={handleProfileIconClick}
          onClose={() => handleToggleMenu(false)}
        />
      </HamburgerMenu>

      <UpdateProfileImageModal
        isOpen={showProfileModal}
        file={profileFile}
        onClose={() => {
          setShowProfileModal(false);
          setProfileFile(null);
        }}
        onSubmit={handleProfileSubmit}
        onDelete={handleProfileDelete}
        calling={calling}
      />
    </>
  );
};

export default CustomSidebar;
